import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, Gavel, ClipboardList, Scale, Search, Plus, Edit3, Trash2, X,
  Calendar, User, Users, MessageSquare, CheckCircle, Clock, AlertCircle,
  ArrowRight, RefreshCw, Save, Filter, ChevronDown, ChevronUp, Globe
} from 'lucide-react';
import HijriDatePicker, { convertHijriToGregorian, convertGregorianToHijri } from '../components/HijriDatePicker';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WorkScheduleTables() {
  const navigate = useNavigate();
  const { t, language, isRTL } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cases');
  const [data, setData] = useState({
    cases: [],
    execution: [],
    reviews: [],
    appeals: []
  });
  const [employees, setEmployees] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // فلاتر التصفية
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    case_type: '',
    court_name: '',
    plaintiff_name: '',
    defendant_name: '',
    responsible_person: '',
    task_consultant: ''
  });
  
  // نافذة الإضافة/التعديل
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    schedule_type: 'cases',
    work_title: '',
    task_description: '',
    date_hijri: '',
    date_gregorian: '',
    responsible_persons: ['', '', ''],
    task_consultant: '',
    notes: '',
    status: 'pending',
    // الحقول الجديدة
    case_number: '',
    case_type: '',
    court_name: '',
    plaintiff_name: '',
    plaintiff_role: 'client', // client أو opponent
    defendant_name: '',
    defendant_role: 'opponent' // client أو opponent
  });
  const [saving, setSaving] = useState(false);

  // نافذة تأكيد الحذف
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);

  // التبويبات مع الترجمة
  const tabs = [
    { id: 'cases', labelKey: 'workSchedule.cases', icon: Gavel, color: 'green' },
    { id: 'execution', labelKey: 'workSchedule.execution', icon: FileText, color: 'blue' },
    { id: 'reviews', labelKey: 'workSchedule.reviews', icon: ClipboardList, color: 'purple' },
    { id: 'appeals', labelKey: 'workSchedule.appeals', icon: Scale, color: 'gold' }
  ];

  // حالات العمل مع الترجمة
  const statusOptions = [
    { value: 'pending', labelKey: 'workSchedule.pending', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'in_progress', labelKey: 'workSchedule.inProgress', color: 'bg-blue-100 text-blue-700' },
    { value: 'completed', labelKey: 'workSchedule.completed', color: 'bg-green-100 text-green-700' }
  ];

  // أنواع القضايا مع الترجمة
  const caseTypes = [
    { value: 'جنائية', labelKey: 'caseType.criminal' },
    { value: 'مدنية', labelKey: 'caseType.civil' },
    { value: 'تجارية', labelKey: 'caseType.commercial' },
    { value: 'عمالية', labelKey: 'caseType.labor' },
    { value: 'أحوال شخصية', labelKey: 'caseType.personalStatus' },
    { value: 'إدارية', labelKey: 'caseType.administrative' },
    { value: 'تنفيذ', labelKey: 'caseType.execution' },
    { value: 'استئناف', labelKey: 'caseType.appeal' },
    { value: 'عليا', labelKey: 'caseType.supreme' },
    { value: 'أخرى', labelKey: 'caseType.other' }
  ];

  // الجهات القضائية مع الترجمة
  const courtOptions = [
    { value: 'المحكمة الجزائية', labelKey: 'court.criminal' },
    { value: 'المحكمة العامة', labelKey: 'court.general' },
    { value: 'المحكمة التجارية', labelKey: 'court.commercial' },
    { value: 'المحكمة العمالية', labelKey: 'court.labor' },
    { value: 'محكمة الأحوال الشخصية', labelKey: 'court.personalStatus' },
    { value: 'المحكمة الإدارية', labelKey: 'court.administrative' },
    { value: 'محكمة التنفيذ', labelKey: 'court.execution' },
    { value: 'محكمة الاستئناف', labelKey: 'court.appeal' },
    { value: 'المحكمة العليا', labelKey: 'court.supreme' },
    { value: 'ديوان المظالم', labelKey: 'court.supreme' },
    { value: 'لجنة المنازعات المصرفية', labelKey: 'court.commercial' },
    { value: 'أخرى', labelKey: 'caseType.other' }
  ];

  useEffect(() => {
    fetchData();
    fetchEmployees();
    fetchConsultants();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/work-schedules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err) {
      console.error('Error fetching schedules:', err);
    }
    setLoading(false);
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data.employees || []);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchConsultants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/consultants/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConsultants(response.data.consultants || []);
    } catch (err) {
      console.error('Error fetching consultants:', err);
    }
  };

  const getStatusBadge = (status) => {
    const statusInfo = statusOptions.find(s => s.value === status) || statusOptions[0];
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
        {t(statusInfo.labelKey)}
      </span>
    );
  };

  const openAddModal = () => {
    setEditingEntry(null);
    setFormData({
      schedule_type: activeTab,
      work_title: '',
      task_description: '',
      date_hijri: '',
      date_gregorian: '',
      responsible_persons: ['', '', ''],
      task_consultant: '',
      notes: '',
      status: 'pending',
      case_number: '',
      case_type: '',
      court_name: '',
      plaintiff_name: '',
      plaintiff_role: 'client',
      defendant_name: '',
      defendant_role: 'opponent'
    });
    setShowModal(true);
  };

  const openEditModal = (entry) => {
    setEditingEntry(entry);
    setFormData({
      schedule_type: entry.schedule_type,
      work_title: entry.work_title || '',
      task_description: entry.task_description || '',
      date_hijri: entry.date_hijri || '',
      date_gregorian: entry.date_gregorian || '',
      responsible_persons: entry.responsible_persons?.length >= 3 
        ? entry.responsible_persons 
        : [...(entry.responsible_persons || []), '', '', ''].slice(0, 3),
      task_consultant: entry.task_consultant || '',
      notes: entry.notes || '',
      status: entry.status || 'pending',
      case_number: entry.case_number || '',
      case_type: entry.case_type || '',
      court_name: entry.court_name || '',
      plaintiff_name: entry.plaintiff_name || '',
      plaintiff_role: entry.plaintiff_role || 'client',
      defendant_name: entry.defendant_name || '',
      defendant_role: entry.defendant_role || 'opponent'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.work_title || !formData.task_description) {
      alert('يرجى ملء الحقول المطلوبة');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        responsible_persons: formData.responsible_persons.filter(p => p.trim() !== '')
      };

      if (editingEntry) {
        await axios.put(`${API}/work-schedules/${editingEntry.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('تم تحديث الإدخال بنجاح');
      } else {
        await axios.post(`${API}/work-schedules`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('تم إضافة الإدخال بنجاح');
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      console.error('Error saving entry:', err);
      alert('حدث خطأ أثناء الحفظ');
    }
    setSaving(false);
  };

  const confirmDelete = (entry) => {
    setDeletingEntry(entry);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deletingEntry) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/work-schedules/${deletingEntry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('تم حذف الإدخال بنجاح');
      setShowDeleteConfirm(false);
      setDeletingEntry(null);
      fetchData();
    } catch (err) {
      console.error('Error deleting entry:', err);
      alert('حدث خطأ أثناء الحذف');
    }
  };

  const updateResponsiblePerson = (index, value) => {
    const newPersons = [...formData.responsible_persons];
    newPersons[index] = value;
    setFormData({ ...formData, responsible_persons: newPersons });
  };

  const getCurrentEntries = () => {
    let entries = data[activeTab] || [];
    
    // تطبيق البحث النصي
    if (searchQuery) {
      entries = entries.filter(entry =>
        entry.work_title?.includes(searchQuery) ||
        entry.task_description?.includes(searchQuery) ||
        entry.notes?.includes(searchQuery) ||
        entry.case_number?.includes(searchQuery) ||
        entry.plaintiff_name?.includes(searchQuery) ||
        entry.defendant_name?.includes(searchQuery) ||
        entry.responsible_persons?.some(p => p.includes(searchQuery))
      );
    }
    
    // تطبيق فلتر التاريخ (من)
    if (filters.date_from) {
      entries = entries.filter(entry => {
        if (!entry.date_gregorian) return false;
        return entry.date_gregorian >= filters.date_from;
      });
    }
    
    // تطبيق فلتر التاريخ (إلى)
    if (filters.date_to) {
      entries = entries.filter(entry => {
        if (!entry.date_gregorian) return false;
        return entry.date_gregorian <= filters.date_to;
      });
    }
    
    // تطبيق فلتر نوع القضية
    if (filters.case_type) {
      entries = entries.filter(entry => entry.case_type === filters.case_type);
    }
    
    // تطبيق فلتر الجهة
    if (filters.court_name) {
      entries = entries.filter(entry => entry.court_name === filters.court_name);
    }
    
    // تطبيق فلتر المدعي
    if (filters.plaintiff_name) {
      entries = entries.filter(entry => 
        entry.plaintiff_name?.includes(filters.plaintiff_name)
      );
    }
    
    // تطبيق فلتر المدعى عليه
    if (filters.defendant_name) {
      entries = entries.filter(entry => 
        entry.defendant_name?.includes(filters.defendant_name)
      );
    }
    
    // تطبيق فلتر المسؤول الأول
    if (filters.responsible_person) {
      entries = entries.filter(entry => 
        entry.responsible_persons?.[0]?.includes(filters.responsible_person)
      );
    }
    
    // تطبيق فلتر مستشار المهمة
    if (filters.task_consultant) {
      entries = entries.filter(entry => 
        entry.task_consultant?.includes(filters.task_consultant)
      );
    }
    
    return entries;
  };

  // إعادة تعيين الفلاتر
  const resetFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      case_type: '',
      court_name: '',
      plaintiff_name: '',
      defendant_name: '',
      responsible_person: '',
      task_consultant: ''
    });
  };

  // التحقق من وجود فلاتر نشطة
  const hasActiveFilters = () => {
    return Object.values(filters).some(v => v !== '');
  };

  const getTabColor = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    return tab?.color || 'gray';
  };

  // جدول العرض
  const ScheduleTable = ({ entries }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">رقم القضية</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">نوع القضية</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">الجهة</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">أطراف القضية</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">العمل</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">المهمة</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">التاريخ</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">المسؤولين</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">الحالة</th>
            <th className="px-3 py-3 text-right font-semibold text-gray-600 text-xs">إجراءات</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {entries.map((entry) => (
            <tr key={entry.id} className="hover:bg-gray-50">
              <td className="px-3 py-3 text-sm">
                {entry.case_number || '-'}
              </td>
              <td className="px-3 py-3 text-sm">
                {entry.case_type && (
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                    {entry.case_type}
                  </span>
                )}
              </td>
              <td className="px-3 py-3 text-xs">
                {entry.court_name || '-'}
              </td>
              <td className="px-3 py-3">
                <div className="space-y-1 text-xs">
                  {entry.plaintiff_name && (
                    <div className="flex items-center gap-1">
                      <span className={`px-1.5 py-0.5 rounded ${entry.plaintiff_role === 'client' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {entry.plaintiff_role === 'client' ? 'موكل' : 'خصم'}
                      </span>
                      <span>مدعي: {entry.plaintiff_name}</span>
                    </div>
                  )}
                  {entry.defendant_name && (
                    <div className="flex items-center gap-1">
                      <span className={`px-1.5 py-0.5 rounded ${entry.defendant_role === 'client' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {entry.defendant_role === 'client' ? 'موكل' : 'خصم'}
                      </span>
                      <span>مدعى عليه: {entry.defendant_name}</span>
                    </div>
                  )}
                  {!entry.plaintiff_name && !entry.defendant_name && '-'}
                </div>
              </td>
              <td className="px-3 py-3 font-medium text-primary text-sm max-w-[120px]">
                <p className="truncate">{entry.work_title}</p>
              </td>
              <td className="px-3 py-3 max-w-[150px]">
                <p className="truncate text-xs text-gray-600">{entry.task_description}</p>
              </td>
              <td className="px-3 py-3 text-xs whitespace-nowrap">
                <div>{entry.date_hijri || '-'}</div>
                <div className="text-gray-400">{entry.date_gregorian || ''}</div>
              </td>
              <td className="px-3 py-3">
                <div className="space-y-0.5">
                  {entry.responsible_persons?.filter(p => p).slice(0, 2).map((person, idx) => (
                    <span key={idx} className="block text-xs bg-gray-100 px-1.5 py-0.5 rounded truncate max-w-[100px]">
                      {person}
                    </span>
                  ))}
                  {entry.responsible_persons?.filter(p => p).length > 2 && (
                    <span className="text-xs text-gray-400">+{entry.responsible_persons.filter(p => p).length - 2}</span>
                  )}
                </div>
              </td>
              <td className="px-3 py-3">
                {getStatusBadge(entry.status)}
              </td>
              <td className="px-3 py-3">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(entry)}
                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="تعديل"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => confirmDelete(entry)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="حذف"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {entries.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <ClipboardList className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p>لا توجد إدخالات في هذا الجدول</p>
          <button
            onClick={openAddModal}
            className="mt-4 px-4 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            إضافة إدخال جديد
          </button>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-gold mx-auto mb-4" />
          <p className="text-gray-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir="rtl">
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white/80 hover:text-white"
            >
              <ArrowRight className="w-5 h-5" />
              {t('workSchedule.backToDashboard')}
            </button>
            <h1 className="text-xl font-heading font-bold">{t('workSchedule.title')}</h1>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="minimal" className="text-white" />
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-gold rounded-lg hover:bg-gold-dark transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t('btn.addNew')}
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {t('btn.refresh')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* الإحصائيات */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            const count = data[tab.id]?.length || 0;
            return (
              <div
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`bg-white rounded-xl p-4 shadow-lg cursor-pointer transition-all ${
                  activeTab === tab.id ? `ring-2 ring-${tab.color}-500` : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{t(tab.labelKey)}</p>
                    <p className="text-2xl font-bold text-primary">{count}</p>
                  </div>
                  <div className={`p-3 bg-${tab.color}-100 rounded-lg`}>
                    <TabIcon className={`w-6 h-6 text-${tab.color}-600`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* التبويبات والمحتوى */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* التبويبات */}
          <div className="flex border-b">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-4 px-6 font-semibold transition-colors flex items-center justify-center gap-2 ${
                    activeTab === tab.id
                      ? `bg-${tab.color}-50 text-${tab.color}-700 border-b-2 border-${tab.color}-500`
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <TabIcon className="w-5 h-5" />
                  {t(tab.labelKey)} ({data[tab.id]?.length || 0})
                </button>
              );
            })}
          </div>

          {/* البحث والفلاتر */}
          <div className="p-4 bg-gray-50 border-b space-y-4">
            <div className="flex items-center gap-4">
              {/* البحث */}
              <div className={`relative flex-1 max-w-md ${isRTL ? '' : 'order-first'}`}>
                <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('filter.searchPlaceholder')}
                  className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none`}
                />
              </div>
              
              {/* زر الفلاتر */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                  showFilters || hasActiveFilters()
                    ? 'bg-gold text-white border-gold'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-gold'
                }`}
              >
                <Filter className="w-4 h-4" />
                {t('btn.advancedFilter')}
                {hasActiveFilters() && (
                  <span className="bg-white text-gold text-xs px-1.5 py-0.5 rounded-full font-bold">
                    {Object.values(filters).filter(v => v !== '').length}
                  </span>
                )}
              </button>
              
              {/* زر إعادة التعيين */}
              {hasActiveFilters() && (
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                  {t('btn.clearFilters')}
                </button>
              )}
            </div>
            
            {/* قسم الفلاتر المتقدمة */}
            {showFilters && (
              <div className="bg-white rounded-xl p-4 border shadow-sm space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                    <Filter className="w-4 h-4" />
                    {t('filter.title')}
                  </h4>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* فلتر التاريخ من */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.dateFrom')}</label>
                    <input
                      type="date"
                      value={filters.date_from}
                      onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    />
                  </div>
                  
                  {/* فلتر التاريخ إلى */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('filter.dateTo')}</label>
                    <input
                      type="date"
                      value={filters.date_to}
                      onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    />
                  </div>
                  
                  {/* فلتر نوع القضية */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('workSchedule.caseType')}</label>
                    <select
                      value={filters.case_type}
                      onChange={(e) => setFilters({ ...filters, case_type: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    >
                      <option value="">الكل</option>
                      {caseTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* فلتر الجهة */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">الجهة</label>
                    <select
                      value={filters.court_name}
                      onChange={(e) => setFilters({ ...filters, court_name: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    >
                      <option value="">الكل</option>
                      {courtOptions.map(court => (
                        <option key={court} value={court}>{court}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* فلتر المدعي */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">المدعي</label>
                    <input
                      type="text"
                      value={filters.plaintiff_name}
                      onChange={(e) => setFilters({ ...filters, plaintiff_name: e.target.value })}
                      placeholder="اسم المدعي..."
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    />
                  </div>
                  
                  {/* فلتر المدعى عليه */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">المدعى عليه</label>
                    <input
                      type="text"
                      value={filters.defendant_name}
                      onChange={(e) => setFilters({ ...filters, defendant_name: e.target.value })}
                      placeholder="اسم المدعى عليه..."
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    />
                  </div>
                  
                  {/* فلتر المسؤول الأول */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">المسؤول الأول</label>
                    <select
                      value={filters.responsible_person}
                      onChange={(e) => setFilters({ ...filters, responsible_person: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    >
                      <option value="">الكل</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.full_name}>{emp.full_name}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* فلتر مستشار المهمة */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">مستشار المهمة</label>
                    <select
                      value={filters.task_consultant}
                      onChange={(e) => setFilters({ ...filters, task_consultant: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-gold outline-none"
                    >
                      <option value="">الكل</option>
                      {consultants.map(con => (
                        <option key={con.id} value={con.full_name}>{con.full_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* عدد النتائج */}
                <div className="pt-2 border-t text-sm text-gray-500">
                  عدد النتائج: <span className="font-bold text-primary">{getCurrentEntries().length}</span> من أصل {data[activeTab]?.length || 0}
                </div>
              </div>
            )}
          </div>

          {/* الجدول */}
          <div className="p-4">
            <ScheduleTable entries={getCurrentEntries()} />
          </div>
        </div>
      </main>

      {/* نافذة الإضافة/التعديل */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
              <h3 className="text-lg font-heading font-bold text-primary">
                {editingEntry ? 'تعديل الإدخال' : 'إضافة إدخال جديد'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* نوع الجدول */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">نوع الجدول</label>
                <select
                  value={formData.schedule_type}
                  onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                >
                  {tabs.map(tab => (
                    <option key={tab.id} value={tab.id}>{tab.label}</option>
                  ))}
                </select>
              </div>

              {/* رقم القضية ونوعها */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">رقم القضية</label>
                  <input
                    type="text"
                    value={formData.case_number}
                    onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                    placeholder="أدخل رقم القضية"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">نوع القضية</label>
                  <select
                    value={formData.case_type}
                    onChange={(e) => setFormData({ ...formData, case_type: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                  >
                    <option value="">اختر نوع القضية</option>
                    {caseTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* الجهة التابعة لها */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الجهة التابعة لها</label>
                <select
                  value={formData.court_name}
                  onChange={(e) => setFormData({ ...formData, court_name: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                >
                  <option value="">اختر الجهة</option>
                  {courtOptions.map(court => (
                    <option key={court} value={court}>{court}</option>
                  ))}
                </select>
              </div>

              {/* أطراف القضية */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <h4 className="font-semibold text-gray-700 flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  أطراف القضية
                </h4>
                
                {/* المدعي */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">المدعي</label>
                    <input
                      type="text"
                      value={formData.plaintiff_name}
                      onChange={(e) => setFormData({ ...formData, plaintiff_name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                      placeholder="اسم المدعي"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">صفة المدعي</label>
                    <div className="flex gap-4">
                      <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.plaintiff_role === 'client' 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="plaintiff_role"
                          value="client"
                          checked={formData.plaintiff_role === 'client'}
                          onChange={(e) => setFormData({ ...formData, plaintiff_role: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">موكلنا</span>
                      </label>
                      <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.plaintiff_role === 'opponent' 
                          ? 'border-red-500 bg-red-50 text-red-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="plaintiff_role"
                          value="opponent"
                          checked={formData.plaintiff_role === 'opponent'}
                          onChange={(e) => setFormData({ ...formData, plaintiff_role: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">الخصم</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* المدعى عليه */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">المدعى عليه</label>
                    <input
                      type="text"
                      value={formData.defendant_name}
                      onChange={(e) => setFormData({ ...formData, defendant_name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                      placeholder="اسم المدعى عليه"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">صفة المدعى عليه</label>
                    <div className="flex gap-4">
                      <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.defendant_role === 'client' 
                          ? 'border-green-500 bg-green-50 text-green-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="defendant_role"
                          value="client"
                          checked={formData.defendant_role === 'client'}
                          onChange={(e) => setFormData({ ...formData, defendant_role: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">موكلنا</span>
                      </label>
                      <label className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.defendant_role === 'opponent' 
                          ? 'border-red-500 bg-red-50 text-red-700' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                        <input
                          type="radio"
                          name="defendant_role"
                          value="opponent"
                          checked={formData.defendant_role === 'opponent'}
                          onChange={(e) => setFormData({ ...formData, defendant_role: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">الخصم</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* العمل */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العمل <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.work_title}
                  onChange={(e) => setFormData({ ...formData, work_title: e.target.value })}
                  required
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                  placeholder="عنوان العمل"
                />
              </div>

              {/* المهمة المطلوبة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المهمة المطلوبة <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.task_description}
                  onChange={(e) => setFormData({ ...formData, task_description: e.target.value })}
                  required
                  rows={3}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none resize-none"
                  placeholder="وصف المهمة المطلوبة..."
                />
              </div>

              {/* التواريخ */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">التاريخ الهجري</label>
                  <HijriDatePicker
                    value={formData.date_hijri}
                    onChange={(hijriDate) => {
                      const gregorianDate = convertHijriToGregorian(hijriDate);
                      setFormData({ 
                        ...formData, 
                        date_hijri: hijriDate,
                        date_gregorian: gregorianDate
                      });
                    }}
                    placeholder="اختر التاريخ الهجري"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">التاريخ الميلادي</label>
                  <div className="relative">
                    <input
                      type="date"
                      value={formData.date_gregorian}
                      onChange={(e) => {
                        const gregorianDate = e.target.value;
                        const hijriDate = convertGregorianToHijri(gregorianDate);
                        setFormData({ 
                          ...formData, 
                          date_gregorian: gregorianDate,
                          date_hijri: hijriDate
                        });
                      }}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* المسؤولين (3 خانات) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">المسؤولين</label>
                <div className="space-y-2">
                  {[0, 1, 2].map((index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 w-6">{index + 1}.</span>
                      <select
                        value={formData.responsible_persons[index] || ''}
                        onChange={(e) => updateResponsiblePerson(index, e.target.value)}
                        className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                      >
                        <option value="">اختر المسؤول</option>
                        {employees.map(emp => (
                          <option key={emp.id} value={emp.full_name}>{emp.full_name}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>

              {/* مستشار المهمة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">مستشار المهمة</label>
                <select
                  value={formData.task_consultant}
                  onChange={(e) => setFormData({ ...formData, task_consultant: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                >
                  <option value="">اختر المستشار</option>
                  {consultants.map(con => (
                    <option key={con.id} value={con.full_name}>{con.full_name}</option>
                  ))}
                </select>
              </div>

              {/* ملاحظات */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">ملاحظات</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none resize-none"
                  placeholder="أي ملاحظات إضافية..."
                />
              </div>

              {/* الحالة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">الحالة</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                >
                  {statusOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* أزرار الإجراء */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2 bg-gold text-white rounded-lg hover:bg-gold-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'جاري الحفظ...' : (editingEntry ? 'تحديث' : 'إضافة')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* نافذة تأكيد الحذف */}
      {showDeleteConfirm && deletingEntry && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-primary mb-2">تأكيد الحذف</h3>
              <p className="text-gray-600 mb-6">
                هل أنت متأكد من حذف "{deletingEntry.work_title}"؟
                <br />
                <span className="text-sm text-red-500">لا يمكن التراجع عن هذا الإجراء</span>
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  حذف
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
